import { isError, LoggableError } from '../../../../../shared/helpers/errors';
import { CompositeRecorderBackend, CompositeRecorderStage } from './types';

type ErrorCode = string | number;
type PlainObject = Record<string, unknown>;

interface RecorderErrorDetails {
    source?: string;
    name?: string;
    message?: string;
    code?: ErrorCode;
    isQuotaExceeded: boolean;
    stack?: string;
}

export interface RecorderErrorContext {
    backend: CompositeRecorderBackend;
    stage: CompositeRecorderStage;
}

interface RecorderLoggableErrorOptions {
    message?: string;
    additional?: Record<string, unknown>;
}

const isPlainObject = (value: unknown): value is PlainObject => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const getNestedError = (error: unknown): unknown => {
    if (!isPlainObject(error)) {
        return undefined;
    }

    return error.error;
};

const toStringField = (value: unknown): string | undefined => {
    return typeof value === 'string' && value ? value : undefined;
};

const toCodeField = (value: unknown): ErrorCode | undefined => {
    if (typeof value === 'number') {
        return value;
    }

    return typeof value === 'string' && value ? value : undefined;
};

const getStringField = (source: unknown, field: string): string | undefined => {
    if (!isPlainObject(source)) {
        return undefined;
    }

    return toStringField(source[field]);
};

const getCodeField = (source: unknown, field: string): ErrorCode | undefined => {
    if (!isPlainObject(source)) {
        return undefined;
    }

    return toCodeField(source[field]);
};

const includesQuotaExceeded = (value: unknown): boolean => {
    return typeof value === 'string' && value.toLowerCase().includes('quota');
};

const getRecorderErrorDetails = (error: unknown): RecorderErrorDetails => {
    const nestedError = getNestedError(error);
    const name = isError(error) ? error.name : getStringField(error, 'name') || getStringField(nestedError, 'name');
    const message =
        typeof error === 'string'
            ? error
            : isError(error)
              ? error.message
              : getStringField(error, 'message') ||
                getStringField(nestedError, 'message') ||
                getStringField(error, 'reason') ||
                getStringField(nestedError, 'reason');
    const code =
        getCodeField(error, 'code') ??
        getCodeField(nestedError, 'code') ??
        getCodeField(error, 'status') ??
        getCodeField(nestedError, 'status');

    return {
        source: getStringField(error, 'source') || getStringField(nestedError, 'source'),
        name,
        message,
        code,
        isQuotaExceeded:
            name === 'QuotaExceededError' || code === 'QuotaExceededError' || includesQuotaExceeded(message),
        stack: isError(error) ? error.stack : undefined,
    };
};

const getRecorderErrorAdditional = (details: RecorderErrorDetails, { backend, stage }: RecorderErrorContext) => {
    return {
        recorder_backend: backend,
        recorder_stage: stage,
        ...(details.source !== undefined ? { recorder_error_source: details.source } : {}),
        ...(details.name !== undefined ? { recorder_error_name: details.name } : {}),
        ...(details.message !== undefined ? { recorder_error_message: details.message } : {}),
        ...(details.code !== undefined ? { recorder_error_code: details.code } : {}),
        is_quota_exceeded: details.isQuotaExceeded,
    };
};

const getRecorderErrorAdditionalFromError = (error: unknown, context: RecorderErrorContext) => {
    return getRecorderErrorAdditional(getRecorderErrorDetails(error), context);
};

export const getRecorderErrorDedupKey = (error: unknown, context: RecorderErrorContext) => {
    const additional = getRecorderErrorAdditionalFromError(error, context);

    return JSON.stringify([
        additional.recorder_backend,
        additional.recorder_stage,
        additional.recorder_error_source,
        additional.recorder_error_name,
        additional.recorder_error_message,
        additional.recorder_error_code,
        additional.is_quota_exceeded,
    ]);
};

export const toRecorderLoggableError = (
    error: unknown,
    context: RecorderErrorContext,
    options: RecorderLoggableErrorOptions = {}
) => {
    const details = getRecorderErrorDetails(error);

    return new LoggableError(
        options.message || `Recorder ${context.backend} ${context.stage} failed`,
        {
            additional: {
                ...getRecorderErrorAdditional(details, context),
                ...options.additional,
            },
        },
        details.stack
    );
};
